import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    CalendarCheck,
    MessageCircle,
    Star,
    ArrowDown,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import StudioGallery from "@/components/StudioGallery";
import Faq from "@/components/Faq";
import SectionHeading from "@/components/SectionHeading";
import ImageSlot from "@/components/ImageSlot";
import { FadeUp, MaskedLine, Stagger, StaggerItem } from "@/components/Reveal";
import {
    CONTACTS,
    FEATURED_SERVICES,
    IMAGES,
    MANIFESTO,
    MARQUEE_WORDS,
    REVIEWS,
    SERVICES,
    whatsappLink,
} from "@/data/site";

const FEATURED_IMAGES = {
    "protesi-laboratorio": "/images/svc-protesi.webp",
    implantologia: "/images/svc-implantologia.webp",
    ortodonzia: "/images/svc-ortodonzia.webp",
    estetica: "/images/svc-estetica.webp",
    "igiene-prevenzione": "/images/svc-igiene.webp",
    endodonzia: "/images/svc-endodonzia.webp",
};

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden pt-32 pb-16 sm:pt-36 md:pb-24"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch gap-14 px-4 sm:px-8 md:px-12 lg:grid-cols-12">
                <div className="flex flex-col lg:col-span-6 lg:justify-between">
                    <FadeUp y={14}>
                        <p
                            data-testid="hero-overline"
                            className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol"
                        >
                            <span className="h-px w-10 bg-petrol" />
                            Studio dentistico · Concorezzo (MB)
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.15}>
                        <img
                            src="/images/logo.png"
                            alt="Studio Dentistico dott. Mario Massironi"
                            data-testid="hero-title"
                            className="mt-7 h-28 w-auto object-contain object-left sm:h-36 lg:h-48"
                        />
                    </FadeUp>

                    <FadeUp delay={0.55}>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-7 max-w-xl text-base font-light leading-relaxed tracking-wide text-body sm:text-lg"
                        >
                            Precisione clinica e attenzione alla persona.
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.7}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contatti"
                                data-testid="hero-cta-prenota"
                                className="inline-flex items-center gap-3 rounded-full bg-petrol px-8 py-4 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                            >
                                <CalendarCheck className="h-4 w-4" />
                                Prenota la prima visita
                            </Link>
                            <a
                                href={whatsappLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-whatsapp"
                                className="inline-flex items-center gap-2.5 rounded-full border border-wa/60 px-7 py-4 text-sm font-normal text-wa-dark transition-all duration-300 hover:bg-wa hover:text-white active:scale-[0.98]"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Scrivici su WhatsApp
                            </a>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.8}>
                        <div
                            data-testid="hero-trust"
                            className="mt-7 flex flex-wrap items-center gap-2"
                        >
                            <span className="flex gap-0.5 text-petrol" aria-hidden="true">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Star key={i} className="h-4 w-4 fill-current" />
                                ))}
                            </span>
                            <span className="text-sm font-normal text-ink">5,0 su 5 su Google</span>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.9}>
                        <dl
                            data-testid="hero-stats"
                            className="mt-12 grid grid-cols-3 gap-x-4 gap-y-8 border-t border-line pt-8"
                        >
                            {[
                                ["1990", "Apertura dello studio"],
                                ["12", "Branche odontoiatriche"],
                                ["35+", "Anni accanto ai nostri pazienti"],
                            ].map(([n, label], i) => (
                                <div
                                    key={label}
                                    className={i > 0 ? "sm:border-l sm:border-line sm:pl-6" : ""}
                                >
                                    <dt className="sr-only">{label}</dt>
                                    <dd className="font-serif text-3xl font-normal text-ink">
                                        {n}
                                    </dd>
                                    <dd className="mt-1.5 break-words text-[0.7rem] font-normal uppercase leading-snug tracking-[0.05em] text-body sm:tracking-[0.14em]">
                                        {label}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </FadeUp>
                </div>

                <div className="relative lg:col-span-6">
                    <motion.div
                        style={{ y: yImg }}
                        className="lg:w-[calc(50vw-28px)]"
                    >
                        <figure
                            data-testid="hero-image"
                            className="group relative overflow-hidden"
                        >
                            <img
                                src={IMAGES.heroHome}
                                alt="La sala operativa dello Studio Dentistico Massironi"
                                style={{
                                    WebkitMaskImage:
                                        "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 16%, #000 32%)",
                                    maskImage:
                                        "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 16%, #000 32%)",
                                }}
                                className="block h-auto w-full transition-transform duration-[1.4s] ease-[cubic-bezier(0.215,0.61,0.355,1)] group-hover:scale-[1.04]"
                            />
                        </figure>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block"
                aria-hidden="true"
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                >
                    <ArrowDown className="h-4 w-4 text-muted" />
                </motion.div>
            </motion.div>
        </section>
    );
}

function Manifesto() {
    return (
        <section
            id="manifesto"
            data-testid="manifesto-section"
            className="bg-ice py-20 sm:py-28 md:py-32"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                <SectionHeading
                    overline="Il Manifesto"
                    lead="Il modo in cui lavoriamo non è un elenco di servizi: è una promessa che rinnoviamo a ogni paziente."
                    testid="manifesto-heading"
                />
                <Stagger
                    testid="manifesto-grid"
                    className="mt-16 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-4"
                >
                    {MANIFESTO.map((p) => (
                        <StaggerItem key={p.number}>
                            <article
                                data-testid={`manifesto-card-${p.number}`}
                                className="group border-t border-ink/15 pt-8"
                            >
                                <span className="font-serif text-5xl font-light text-petrol/45 transition-colors duration-500 group-hover:text-petrol">
                                    {p.number}
                                </span>
                                <h3 className="mt-6 font-serif text-xl font-medium leading-snug text-ink">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm font-light leading-relaxed tracking-wide text-body">
                                    {p.description}
                                </p>
                            </article>
                        </StaggerItem>
                    ))}
                </Stagger>
            </div>
        </section>
    );
}

function ServiziPreview() {
    const featured = FEATURED_SERVICES.map((id) =>
        SERVICES.find((s) => s.id === id),
    );
    return (
        <section
            data-testid="services-preview-section"
            className="bg-white py-20 sm:py-28 md:py-32"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                <div className="flex flex-wrap items-end justify-between gap-8">
                    <SectionHeading
                        overline="I Servizi"
                        title="Ogni branca, dalla prima visita al sorriso finale."
                        testid="services-preview-heading"
                    />
                    <FadeUp delay={0.2}>
                        <Link
                            to="/servizi"
                            data-testid="services-preview-all-link"
                            className="group inline-flex items-center gap-2 text-sm font-normal text-petrol transition-colors hover:text-petrol-dark"
                        >
                            Tutti i servizi
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </FadeUp>
                </div>

                <Stagger
                    testid="services-preview-grid"
                    className="mt-16 grid grid-cols-1 gap-x-10 gap-y-16 md:grid-cols-2"
                >
                    {featured.map((s) => (
                        <StaggerItem key={s.id}>
                            <Link
                                to={`/servizi#${s.id}`}
                                data-testid={`service-card-${s.id}`}
                                className="group block"
                            >
                                <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-ice">
                                    <img
                                        src={FEATURED_IMAGES[s.id]}
                                        alt={s.name}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.215,0.61,0.355,1)] group-hover:scale-[1.06]"
                                    />
                                </div>
                                <div className="mt-5 flex items-start justify-between gap-4">
                                    <div>
                                        <h3 className="font-serif text-xl font-medium leading-snug text-ink transition-colors duration-300 group-hover:text-petrol">
                                            {s.name}
                                        </h3>
                                        <p className="mt-2 text-sm font-light leading-relaxed tracking-wide text-body">
                                            {s.short}
                                        </p>
                                    </div>
                                    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-petrol transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                                </div>
                            </Link>
                        </StaggerItem>
                    ))}
                </Stagger>
            </div>
        </section>
    );
}

function RecensioniPreview() {
    return (
        <section
            data-testid="reviews-preview-section"
            className="bg-white py-20 sm:py-28 md:py-32"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                <SectionHeading
                    overline="Le Recensioni"
                    light
                    title="La fiducia di chi si è già seduto in poltrona."
                    align="center"
                    testid="reviews-preview-heading"
                />
                <Stagger
                    testid="reviews-preview-grid"
                    className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3"
                >
                    {REVIEWS.slice(0, 3).map((r) => (
                        <StaggerItem key={r.name}>
                            <blockquote
                                data-testid={`review-preview-${r.name.replace(/\W/g, "-").toLowerCase()}`}
                                className="flex h-full flex-col justify-between rounded-2xl bg-ice p-8 transition-colors duration-300"
                            >
                                <div>
                                    <div className="flex gap-1 text-petrol" aria-label={`${r.rating} stelle su 5`}>
                                        {Array.from({ length: r.rating }).map((_, i) => (
                                            <Star key={i} className="h-4 w-4 fill-current" />
                                        ))}
                                    </div>
                                    <p className="mt-5 font-serif text-lg font-normal italic leading-relaxed text-ink">
                                        “{r.text}”
                                    </p>
                                </div>
                                <footer className="mt-8">
                                    <p className="text-sm font-normal text-ink">{r.name}</p>
                                    <p className="mt-0.5 text-xs font-light uppercase tracking-[0.18em] text-muted">
                                        {r.service}
                                    </p>
                                </footer>
                            </blockquote>
                        </StaggerItem>
                    ))}
                </Stagger>
                <FadeUp delay={0.2} className="mt-12 text-center">
                    <Link
                        to="/recensioni"
                        data-testid="reviews-preview-all-link"
                        className="group inline-flex items-center gap-2 text-sm font-normal text-petrol transition-colors hover:text-petrol-dark"
                    >
                        Leggi tutte le recensioni
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </FadeUp>
            </div>
        </section>
    );
}

function CtaFinale() {
    return (
        <section
            data-testid="cta-prima-visita"
            className="bg-ice py-20 sm:py-28 md:py-32"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-8 md:px-12 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <FadeUp>
                        <ImageSlot
                            src={IMAGES.dottoreMonitor}
                            alt="Il Dr. Mario Massironi mentre illustra una radiografia"
                            ratio="aspect-[4/5]"
                            testid="cta-doctor-image"
                        />
                    </FadeUp>
                </div>
                <div className="lg:col-span-7 lg:pl-6">
                    <FadeUp y={14}>
                        <p className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol">
                            <span className="h-px w-10 bg-petrol" />
                            La Prima Visita
                        </p>
                    </FadeUp>
                    <h2 className="mt-6 font-serif text-4xl font-normal leading-[1.1] tracking-tight text-ink sm:text-5xl">
                        <MaskedLine delay={0.1}>È una conversazione,</MaskedLine>
                        <MaskedLine delay={0.25}>
                            <span className="italic text-petrol">non un impegno.</span>
                        </MaskedLine>
                    </h2>
                    <FadeUp delay={0.45}>
                        <p className="mt-6 max-w-xl text-base font-light leading-relaxed tracking-wide text-body sm:text-lg">
                            Ci conosciamo, guardiamo insieme la tua bocca e ti
                            diamo un piano chiaro, con tempi e costi. Poi decidi
                            tu, con calma.
                        </p>
                        <p
                            data-testid="cta-doctor-sign"
                            className="mt-6 font-serif text-xl font-normal italic text-ink"
                        >
                            — Dr. Mario Massironi,{" "}
                            <span className="text-sm not-italic font-light uppercase tracking-[0.18em] text-muted">
                                odontoiatra, odontotecnico e igienista dentale
                            </span>
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.6}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contatti"
                                data-testid="cta-prenota-button"
                                className="inline-flex items-center gap-3 rounded-full bg-petrol px-8 py-4 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                            >
                                <CalendarCheck className="h-4 w-4" />
                                Prenota la prima visita
                            </Link>
                            <a
                                href={CONTACTS.phoneHref}
                                data-testid="cta-phone-link"
                                className="text-sm font-normal text-petrol transition-colors hover:text-petrol-dark"
                            >
                                oppure chiama lo {CONTACTS.phoneDisplay}
                            </a>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee words={MARQUEE_WORDS} />
            <Manifesto />
            <ServiziPreview />
            <StudioGallery showLink stagger={false} />
            <RecensioniPreview />
            <Faq />
            <CtaFinale />
        </>
    );
}
